import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  HelpCircle, Input, Mail,
  MessageCircle,
  MessageSquare,
  Modal,
  Search,
} from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedbackForm } from "../../components/molecules/FeedbackForm/FeedbackForm";
import useManager from "../../hooks/useManager";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface HelpSection {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  items: string[];
}

export const HelpSupport: React.FC = () => {
  const manager = useManager();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const FAQ_DATA: FAQItem[] = [
    {
      id: "1",
      question: t("helpSupport.faq.questions.addDoctor.question"),
      answer: t("helpSupport.faq.questions.addDoctor.answer"),
      category: t("helpSupport.faq.categories.staffManagement"),
    },
    {
      id: "2",
      question: t("helpSupport.faq.questions.scheduleAppointment.question"),
      answer: t("helpSupport.faq.questions.scheduleAppointment.answer"),
      category: t("helpSupport.faq.categories.appointments"),
    },
    {
      id: "3",
      question: t("helpSupport.faq.questions.operatingHours.question"),
      answer: t("helpSupport.faq.questions.operatingHours.answer"),
      category: t("helpSupport.faq.categories.facilitySettings"),
    },
    {
      id: "4",
      question: t("helpSupport.faq.questions.viewReports.question"),
      answer: t("helpSupport.faq.questions.viewReports.answer"),
      category: t("helpSupport.faq.categories.reports"),
    },
    {
      id: "5",
      question: t("helpSupport.faq.questions.emergency.question"),
      answer: t("helpSupport.faq.questions.emergency.answer"),
      category: t("helpSupport.faq.categories.emergency"),
    },
    {
      id: "6",
      question: t("helpSupport.faq.questions.patientRecords.question"),
      answer: t("helpSupport.faq.questions.patientRecords.answer"),
      category: t("helpSupport.faq.categories.patients"),
    },
  ];

  const HELP_SECTIONS: HelpSection[] = [
    {
      id: "getting-started",
      title: t("helpSupport.sections.gettingStarted.title"),
      description: t("helpSupport.sections.gettingStarted.description"),
      icon: BookOpen,
      items: [
        t("helpSupport.sections.gettingStarted.items.setupProfile"),
        t("helpSupport.sections.gettingStarted.items.addDoctor"),
        t("helpSupport.sections.gettingStarted.items.scheduleAppointment"),
        t("helpSupport.sections.gettingStarted.items.dashboard"),
      ],
    },
    {
      id: "staff-management",
      title: t("helpSupport.sections.staffManagement.title"),
      description: t("helpSupport.sections.staffManagement.description"),
      icon: HelpCircle,
      items: [
        t("helpSupport.sections.staffManagement.items.addStaff"),
        t("helpSupport.sections.staffManagement.items.schedules"),
        t("helpSupport.sections.staffManagement.items.permissions"),
        t("helpSupport.sections.staffManagement.items.tracking"),
      ],
    },
    {
      id: "appointments",
      title: t("helpSupport.sections.appointments.title"),
      description: t("helpSupport.sections.appointments.description"),
      icon: MessageCircle,
      items: [
        t("helpSupport.sections.appointments.items.create"),
        t("helpSupport.sections.appointments.items.reschedule"),
        t("helpSupport.sections.appointments.items.conflicts"),
        t("helpSupport.sections.appointments.items.reminders"),
      ],
    },
    {
      id: "facility-settings",
      title: t("helpSupport.sections.facilitySettings.title"),
      description: t("helpSupport.sections.facilitySettings.description"),
      icon: HelpCircle,
      items: [
        t("helpSupport.sections.facilitySettings.items.updateInfo"),
        t("helpSupport.sections.facilitySettings.items.operatingHours"),
        t("helpSupport.sections.facilitySettings.items.services"),
        t("helpSupport.sections.facilitySettings.items.notifications"),
      ],
    },
  ];

  const filteredFAQs = FAQ_DATA.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    "all",
    ...Array.from(new Set(FAQ_DATA.map((faq) => faq.category))),
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-ews-primary-100">
          <HelpCircle className="w-6 h-6 text-primary-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {t("helpSupport.title")}
          </h1>
          <p className="text-neutral-600">{t("helpSupport.subtitle")}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="flex absolute inset-y-0 left-0 items-center pl-3">
          <Search className="w-5 h-5 text-neutral-400" />
        </div>
        <Input
          type="text"
          placeholder={t("helpSupport.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="py-3 pr-4 pl-10 w-full rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="p-4 bg-white rounded-lg border transition-shadow border-neutral-200 hover:shadow-md">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-neutral-900">
                {t("helpSupport.quickActions.email.title")}
              </h3>
              <p className="text-sm text-neutral-500">
                {t("helpSupport.quickActions.email.address")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Help Sections */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-neutral-900">
          {t("helpSupport.helpTopics")}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {HELP_SECTIONS.map((section) => {
            const IconComponent = section.icon;
            return (
              <div
                key={section.id}
                className="p-4 bg-white rounded-lg border transition-shadow border-neutral-200 hover:shadow-md"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-neutral-100">
                    <IconComponent className="w-5 h-5 text-neutral-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-neutral-900">
                      {section.title}
                    </h3>
                    <p className="mb-3 text-sm text-neutral-500">
                      {section.description}
                    </p>
                    <ul className="space-y-1">
                      {section.items.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-center text-sm text-neutral-600"
                        >
                          <ChevronRight className="mr-1 w-3 h-3 text-neutral-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-neutral-900">
            {t("helpSupport.faq.title")}
          </h2>
          <div className="flex space-x-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${selectedCategory === category
                    ? "bg-ews-primary-100 text-primary-700"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                  }`}
              >
                {category === "all" ? t("helpSupport.faq.all") : category}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {filteredFAQs.map((faq) => (
            <div
              key={faq.id}
              className="overflow-hidden bg-white rounded-lg border border-neutral-200"
            >
              <button
                onClick={() =>
                  setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)
                }
                className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-neutral-50"
              >
                <div className="flex-1">
                  <h3 className="font-medium text-neutral-900">
                    {faq.question}
                  </h3>
                  <span className="px-2 py-1 text-xs rounded-full text-neutral-500 bg-neutral-100">
                    {faq.category}
                  </span>
                </div>
                {expandedFAQ === faq.id ? (
                  <ChevronDown className="w-5 h-5 text-neutral-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-neutral-400" />
                )}
              </button>
              {expandedFAQ === faq.id && (
                <div className="px-4 pb-3 border-t border-neutral-100">
                  <p className="mt-3 text-sm text-neutral-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredFAQs.length === 0 && (
          <div className="py-8 text-center">
            <HelpCircle className="mx-auto mb-3 w-12 h-12 text-neutral-300" />
            <p className="text-neutral-500">{t("helpSupport.faq.noResults")}</p>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="p-6 rounded-lg border bg-ews-primary-50 border-primary-200">
        <h3 className="mb-3 font-medium text-ews-primary-900">
          {t("helpSupport.contact.title")}
        </h3>
        <p className="mb-4 text-sm text-primary-700">
          {t("helpSupport.contact.description")}
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <Mail className="w-4 h-4 text-primary-600" />
            <span className="text-primary-700">
              {t("helpSupport.contact.email")}
            </span>
          </div>
        </div>
      </div>

      {/* Send Feedback Section */}
      <div className="p-6 bg-white rounded-lg border border-neutral-200">
        <div className="flex justify-between items-start">
          <div className="flex items-start space-x-4">
            <div className="flex justify-center items-center w-12 h-12 rounded-lg bg-ews-primary-100">
              <MessageSquare className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">
                {t("helpSupport.feedback.title")}
              </h2>
              <p className="mt-1 text-sm text-neutral-600">
                {t("helpSupport.feedback.description")}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
          >
            <MessageSquare className="inline-block mr-2 w-4 h-4" />
            {t("helpSupport.feedback.button")}
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      <Modal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        title={t("helpSupport.feedback.modalTitle")}
        variant="info"
        size="lg"
      >
        <FeedbackForm
          managerId={Number(manager.id)}
          onSuccess={() => setIsFeedbackModalOpen(false)}
          onCancel={() => setIsFeedbackModalOpen(false)}
        />
      </Modal>
    </div>
  );
};
